import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  AuditLogInsertInput,
  Customer,
  Device,
  DeviceInsertInput,
  DeviceMutationResponse,
  SiteInsertInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { createDeviceInBulkMutation } from '../../Customers/graphql/mutation';

import { Button } from '@/components/ui/Button';
import { ComboboxFormField } from '@/components/ui/form-fields/ComboboxFormField';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { TimezoneSelection } from '@/components/ui/time-picker/TimezoneSelection';
import { DEFAULT_DEVICE_CONFIG } from '@/constants/config.constants';
import { devicesByIdsQuery } from '@/features/Customers/graphql/query';
import {
  AuditLogAction,
  AuditLogEntityType,
  AuditLogSubject,
} from '@/models/audit-log.models';
import { DEFAULT_UUID } from '@/models/constants';
import { CustomerConfig } from '@/models/customer.models';
import { PRODUCTS } from '@/models/device.models';
import { SiteConfig } from '@/models/site.models';
import { UTC_ZONE } from '@/models/time.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { generateAlphanumeric } from '@/utils/key.functions';
import { parseLatLng, validateLatLng } from '@/utils/map';
import { Download, Plus, Upload, X } from 'lucide-react';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { createDeviceApiKeysMutation } from '../graphql/mutation';

type FormData = {
  deviceName: string;
  deviceSerial: string;
  siteName: string;
  siteLocation?: string;
};

type CreateDeviceResult = {
  id: string;
  name: string;
  imei: string;
  siteName: string;
  ftpUser: string;
  ftpServer: string;
  ftpPort: string;
  ftpShortUsername: string;
};

interface CreateDevicesDialogProps {
  open: boolean;
  customers: Customer[];
  onToggleOpen: (refetch?: boolean) => void;
}

interface DeviceApiKeyMap {
  [deviceId: string]: { key: string; reference?: string | null };
}

export function AddDevicesDialog({
  open,
  customers,
  onToggleOpen,
}: CreateDevicesDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [parsedData, setParsedData] = useState<FormData[]>();
  const [isDragging, setIsDragging] = useState(false);
  const [_dragCounter, setDragCounter] = useState(0);
  const [createdDeviceIds, setCreatedDeviceIds] = useState<string[]>([]);
  const [deviceApiKeys, setDeviceApiKeys] = useState<DeviceApiKeyMap>({});
  const [createdDevices, setCreatedDevices] = useState<CreateDeviceResult[]>(
    [],
  );

  const {
    register,
    control,
    trigger,
    reset,
    formState: { isValid },
  } = useForm<{
    devices: FormData[];
    products: string[];
    customerId: string;
    timezone: string;
    ocrRegions: string[];
    tunnelUrl?: string;
  }>({
    defaultValues: {
      devices: [],
      customerId: customers[0]?.id,
      products: ['senpic'],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'devices',
  });
  const formValues = useWatch({ control });
  const [suggestConfig, setSuggestConfig] = useState<SiteConfig>(
    customers[0]?.sites?.[0]?.config as SiteConfig,
  );
  const { errors } = useFormState({ control });
  const inputRef = useRef<HTMLInputElement>(null);
  const { isPending: isCreateDevicesPending, mutateAsync: createDevices } =
    useMutation({
      mutationKey: ['CreateSite'],
      mutationFn: (options: {
        deviceInsertInputs: DeviceInsertInput[];
        siteInsertInputs: SiteInsertInput[];
        auditLogInput: AuditLogInsertInput;
      }) => {
        return execute({
          query: createDeviceInBulkMutation,
          variables: {
            ...options,
          },
        });
      },
    });

  const {
    mutateAsync: createDeviceApiKeys,
    isPending: isCreateDeviceApiKeysPending,
  } = useMutation({
    mutationKey: ['CreateDeviceApiKeysMutation'],
    mutationFn: (options: {
      input: {
        createDeviceApiKeys: { deviceId: string; description?: string }[];
      };
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createDeviceApiKeysMutation,
        variables: {
          ...options,
        },
      });
    },
  });

  const {
    data,
    isFetching: fetchingDevices,
    isLoading: firstLoadingDevices,
  } = useQuery({
    queryKey: ['CreatedDevices', createdDeviceIds],
    queryFn: () =>
      execute({
        query: devicesByIdsQuery,
        variables: {
          ids: createdDeviceIds,
        },
      }),
    select: (data) => data,
    enabled: createdDeviceIds.length > 0,
  });

  useEffect(() => {
    if (data) {
      const customer = customers.find((c) => c.id === formValues.customerId);
      const customerConfig = customer?.config as CustomerConfig;
      const downloadData = (data.device as Device[]).map(
        (d): CreateDeviceResult => {
          return {
            id: d.id,
            name: d.name ?? '',
            imei: d.serial,
            siteName: d.site?.name ?? '',
            ftpServer: customerConfig?.Ftp?.Server ?? '',
            ftpPort: customerConfig?.Ftp?.Port ?? '',
            ftpUser: d.id,
            ftpShortUsername: d.shortId ?? '',
          };
        },
      );
      setCreatedDevices(downloadData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (createdDevices?.length > 0) {
      handleDownloadResultsClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdDevices]);

  const handleToggleOpenState = () => {
    if (
      isCreateDevicesPending ||
      isCreateDeviceApiKeysPending ||
      fetchingDevices ||
      firstLoadingDevices
    ) {
      return;
    }
    onToggleOpen();
  };

  useEffect(() => {
    if (parsedData) {
      append(parsedData);
    }
  }, [parsedData, append]);

  useEffect(() => {
    reset({
      ...formValues,
      ocrRegions: suggestConfig?.OcrRegions ?? [],
      tunnelUrl: suggestConfig?.TunnelUrl ?? '',
      timezone: suggestConfig?.Timezone ?? UTC_ZONE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, suggestConfig]);

  useEffect(() => {
    if (formValues) {
      void trigger();
    }
  }, [formValues, trigger]);

  useEffect(() => {
    if (files?.length > 0) {
      const parseCSV = (file: File): Promise<FormData[]> => {
        return new Promise((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const parsedData = results.data;
              const formData = parsedData.map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (item: any): FormData => ({
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  deviceName: item?.DeviceName,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  deviceSerial: item?.IMEI,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  siteName: item?.SiteName,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  siteLocation: item?.SiteLocation,
                }),
              );
              resolve(formData);
            },
            error: (error) => {
              console.error('CSV parsing error:', error);
              reject(error);
            },
          });
        });
      };

      Promise.all(files.map(parseCSV))
        .then((allResults) => {
          const combinedData = allResults.flat();
          setParsedData(combinedData);
        })
        .catch((err) => {
          console.error('Error parsing one or more CSV files:', err);
        });
    }
  }, [files]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const siteConfig: SiteConfig = {
      Timezone: formValues.timezone as string,
      OcrRegions: formValues.ocrRegions as string[],
      TunnelUrl: formValues.tunnelUrl ? formValues.tunnelUrl : '',
    };

    const timestamp = new Date().toISOString();
    const deviceInsertInputs: DeviceInsertInput[] = [];
    const siteInsertInputs: SiteInsertInput[] = [];

    formValues.devices?.forEach((field) => {
      const deviceUuid = uuid();
      let siteUuid = undefined;
      if (field.siteName) {
        siteUuid = uuid();
        const site: SiteInsertInput = {
          id: siteUuid,
          customerId: formValues.customerId,
          name: field.siteName,
          config: siteConfig,
          createdAt: timestamp,
          updatedAt: timestamp,
          rowVersion: DEFAULT_UUID,
        };
        if (field.siteLocation) {
          const parseLocation = parseLatLng(field.siteLocation);
          const geometry: GeoJSON.Geometry = {
            type: 'Point',
            coordinates: [
              parseLocation?.lng as number,
              parseLocation?.lat as number,
            ],
          };
          site.geometry = geometry;
        }
        siteInsertInputs.push(site);
      }

      deviceInsertInputs.push({
        id: deviceUuid,
        shortId: generateAlphanumeric(),
        siteId: siteUuid,
        name: field.deviceName,
        serial: field.deviceSerial,
        data: {},
        config: DEFAULT_DEVICE_CONFIG,
        products: formValues.products,
        createdAt: timestamp,
        updatedAt: timestamp,
        rowVersion: DEFAULT_UUID,
      });
    });

    const insertDevicesLog: AuditLogSubject[] = deviceInsertInputs.map((d) => ({
      id: d.id as string,
      entity: AuditLogEntityType.DEVICE,
    }));
    const insertSitesLog: AuditLogSubject[] = siteInsertInputs.map((s) => ({
      id: s.id as string,
      entity: AuditLogEntityType.SITE,
    }));

    const insertApiKeysLog: AuditLogSubject[] = deviceInsertInputs.map((a) => ({
      id: a.id as string,
      entity: AuditLogEntityType.API_KEY,
    }));

    const createDevicesResult = await createDevices({
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.CREATE_DEVICE_BATCH,
        entities: [...insertDevicesLog, ...insertSitesLog],
      }),
      deviceInsertInputs,
      siteInsertInputs,
    });

    // Create the api keys after devices are created
    const createDeviceApiKeysResult = await createDeviceApiKeys({
      input: {
        createDeviceApiKeys: deviceInsertInputs.map((t) => ({
          deviceId: t.id as string,
        })),
      },
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.CREATE_DEVICE_BATCH,
        entities: [...insertApiKeysLog],
      }),
    });

    const deviceApiKeys: DeviceApiKeyMap = {};
    for (const apiKey of createDeviceApiKeysResult?.createDeviceApiKeys
      ?.apiKeys ?? []) {
      deviceApiKeys[apiKey.deviceId] = apiKey;
    }

    setDeviceApiKeys(deviceApiKeys);

    const deviceIds = (
      createDevicesResult.insertDevice as DeviceMutationResponse
    )?.returning.map((d) => d.id);

    setCreatedDeviceIds(deviceIds);

    toast.success(
      `${deviceIds.length} device${deviceIds.length > 1 && 's'} successfully created`,
      {
        duration: 3000,
      },
    );
  };

  const handleDownloadTemplateClick = () => {
    const link = document.createElement('a');
    link.href = '/files/device-creation-example.csv';
    link.download = 'device_creation_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddDevice = () => {
    append({
      deviceName: '',
      deviceSerial: '',
      siteName: '',
    });
  };

  const handleRemoveDevice = (index: number) => {
    remove(index);
  };

  const handleDragEnter = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setDragCounter((prev) => prev + 1);
    setIsDragging(true);
  };

  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter <= 0) {
        setIsDragging(false);
        return 0;
      }
      return newCounter;
    });
  };

  const handleDrop = (e: {
    preventDefault: () => void;
    dataTransfer: { files: unknown };
  }) => {
    e.preventDefault();
    handleUploadFiles(e.dataTransfer.files);
    setDragCounter(0);
    setIsDragging(false);
  };

  const handleChange = (event: { target: { files: unknown } }) => {
    handleUploadFiles(event.target.files);
  };

  const handleDownloadResultsClick = () => {
    const csv = Papa.unparse({
      fields: [
        'DeviceName',
        'IMEI',
        'SiteName',
        'FtpServer',
        'FtpPort',
        'FtpUser',
        'FtpPassword',
        'FtpShortUsername',
      ],
      data: createdDevices.map((d) => [
        d.name,
        d.imei,
        d.siteName,
        d.ftpServer,
        d.ftpPort,
        d.ftpUser,
        deviceApiKeys[d.id].key,
        d.ftpShortUsername,
      ]),
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'created_devices.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadFiles = (files: unknown) => {
    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const fileArray = Array.from(files as File[]);
    const hasInvalidFiles = fileArray.some(
      (file) => file.type !== 'text/csv' || file.size > maxSizeBytes,
    );
    if (hasInvalidFiles) {
      return;
    }
    setFiles(fileArray);
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleRestartClick = () => {
    setCreatedDeviceIds([]);
    setCreatedDevices([]);
    setDragCounter(0);
    setFiles([]);
    setParsedData(undefined);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="flex h-[90vh] w-[90vw] flex-col sm:max-w-[90vw] lg:max-w-[90vw]">
        <DialogHeader className="relative">
          <div>
            <DialogTitle>Add Devices</DialogTitle>
            <DialogDescription>
              {createdDevices.length > 0
                ? `${createdDevices.length} device${createdDevices.length > 1 ? 's' : ''} created`
                : 'Products, Customer, and Site Config are applied to all entries.'}
            </DialogDescription>
          </div>
          {createdDevices?.length && (
            <div className="absolute top-0 right-0 mt-4 mr-4">
              <p className="text-red font-semibold">
                Passwords are only displayed once here for security reasons.
              </p>
              <p className="text-muted-foreground text-sm">
                Please make sure to save them.
              </p>
            </div>
          )}
        </DialogHeader>
        {createdDevices.length > 0 ? (
          <>
            <div className="flex flex-grow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device Name</TableHead>
                    <TableHead>IMEI</TableHead>
                    <TableHead>Site Name</TableHead>
                    <TableHead>FTP Server</TableHead>
                    <TableHead>FTP Port</TableHead>
                    <TableHead>FTP User</TableHead>
                    <TableHead>FTP Password</TableHead>
                    <TableHead>FTP Short Username</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {createdDevices.map((device) => (
                    <TableRow key={device.name}>
                      <TableCell>{device.name}</TableCell>
                      <TableCell>{device.imei}</TableCell>
                      <TableCell>{device.siteName}</TableCell>
                      <TableCell>{device.ftpServer}</TableCell>
                      <TableCell>{device.ftpPort}</TableCell>
                      <TableCell>{device.ftpUser}</TableCell>
                      <TableCell>{deviceApiKeys[device.id].key}</TableCell>
                      <TableCell>{device.ftpShortUsername}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DialogFooter className="flex w-full justify-end gap-2">
              <Button variant={'outline'} onClick={handleDownloadResultsClick}>
                Download Result in CSV
              </Button>
              <Button onClick={handleRestartClick}>Back to Add Devices</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <input
              type="file"
              className="hidden"
              ref={inputRef}
              multiple={true}
              accept="text/csv"
              onChange={handleChange}
            />
            <form
              onSubmit={(e) => void handleSubmit(e)}
              className="relative flex flex-1 flex-col justify-between gap-2 overflow-hidden"
            >
              <div className="grid gap-2">
                <h3 className="text-muted-foreground text-sm text-balance"></h3>
                <div className="mb-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
                  <Controller
                    name="products"
                    control={control}
                    rules={{ required: 'Products is required' }}
                    render={({ field }) => (
                      <div className="grid gap-2">
                        <Label htmlFor="products">Products</Label>
                        <ComboboxFormField
                          modal={true}
                          hasError={!!errors.products}
                          options={PRODUCTS.map((product) => ({
                            value: product,
                          }))}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />

                  <div className="grid gap-2">
                    <Label htmlFor="customer">Customer</Label>
                    <Controller
                      name="customerId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          onValueChange={(customerId) => {
                            setSuggestConfig(
                              customers.find((c) => c.id === customerId)
                                ?.sites?.[0]?.config as SiteConfig,
                            );
                            return field.onChange(customerId);
                          }}
                          value={field.value}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Filter by customer" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {customers.map((customer) => (
                                <SelectItem
                                  value={customer.id}
                                  key={customer.id}
                                >
                                  {customer.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Controller
                      name="timezone"
                      control={control}
                      rules={{ required: 'Timezone is required' }}
                      render={({ field }) => (
                        <div className="grid gap-2">
                          <Label htmlFor="timezone">Timezone</Label>
                          <TimezoneSelection
                            modal={true}
                            size="sm"
                            allowToggle={false}
                            timezone={field.value}
                            setTimezone={field.onChange}
                          />
                        </div>
                      )}
                    />
                  </div>
                  <Controller
                    name="ocrRegions"
                    control={control}
                    rules={{ required: 'OCR Regions is required' }}
                    render={({ field }) => (
                      <div className="grid gap-2">
                        <Label htmlFor="ocrRegions">OCR Regions</Label>
                        <ComboboxFormField
                          modal={true}
                          hasError={!!errors.ocrRegions}
                          options={[
                            { value: 'NSW' },
                            { value: 'VIC' },
                            { value: 'QLD' },
                            { value: 'SA' },
                            { value: 'WA' },
                            { value: 'NT' },
                            { value: 'TAS' },
                            { value: 'ACT' },
                          ]}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                  <div className="grid gap-2">
                    <Label htmlFor="tunnelUrl">Tunnel URL</Label>
                    <Input
                      type="text"
                      id="tunnelUrl"
                      {...(register('tunnelUrl'),
                      {
                        required: false,
                      })}
                      placeholder="E.g. https://localhost:3000/tunnel"
                    />
                  </div>
                </div>
              </div>
              <Separator orientation="horizontal" />
              <h3 className="text-muted-foreground text-sm text-balance">
                Drag & drop template files, or click Browse Files to upload (CSV
                only, max 5MB).
              </h3>
              <div
                className={`flex-1 overflow-auto p-2 ${isDragging && 'bg-muted rounded border border-dashed'}`}
                role="button"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="mb-4 grid grid-cols-[repeat(4,minmax(125px,1fr))_40px] gap-4">
                  <div className="text-sm leading-none font-medium">Name*</div>
                  <div className="text-sm leading-none font-medium">
                    Serial*
                  </div>
                  <div className="text-sm leading-none font-medium">
                    Site Name
                  </div>
                  <div className="text-sm leading-none font-medium">
                    Location (lat, lng)
                  </div>
                </div>
                {fields.map((_field, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[repeat(4,minmax(125px,1fr))_40px] gap-4 space-y-3"
                  >
                    <div>
                      <Input
                        type="text"
                        id="deviceName"
                        placeholder="Device name"
                        className={
                          errors.devices?.[index]?.deviceName &&
                          'border-destructive'
                        }
                        {...register(`devices.${index}.deviceName`, {
                          required: true,
                        })}
                      />
                    </div>
                    <div>
                      <Input
                        type="text"
                        id="serial"
                        placeholder="Serial"
                        className={
                          errors.devices?.[index]?.deviceSerial &&
                          'border-destructive'
                        }
                        {...register(`devices.${index}.deviceSerial`, {
                          required: true,
                        })}
                      />
                    </div>

                    <div>
                      <Input
                        type="text"
                        id="siteName"
                        placeholder="Site name"
                        className={` ${
                          errors.devices?.[index]?.siteName &&
                          'border-destructive'
                        }`}
                        {...register(`devices.${index}.siteName`, {
                          validate: (val, formValues) => {
                            const siteLocation =
                              formValues.devices?.[index]?.siteLocation;
                            if (siteLocation && !val) {
                              return 'Site name is required if location is filled';
                            }
                            return true;
                          },
                        })}
                      />
                    </div>
                    <div>
                      <Input
                        type="text"
                        id="siteLocation"
                        placeholder="Location"
                        className={`${
                          errors.devices?.[index]?.siteLocation &&
                          'border-destructive'
                        }`}
                        {...register(`devices.${index}.siteLocation`, {
                          validate: (val) => {
                            if (!val) {
                              return true;
                            } else {
                              return validateLatLng(val);
                            }
                          },
                        })}
                      />
                    </div>

                    <div>
                      <Button
                        type="button"
                        variant={'outline'}
                        size={'icon'}
                        onClick={() => handleRemoveDevice(index)}
                      >
                        <X />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant={'outline'}
                    size={'sm'}
                    className="w-[100px]"
                    onClick={handleAddDevice}
                  >
                    <Plus /> Add
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={'outline'}
                      onClick={handleUploadClick}
                    >
                      <Upload />
                      Browse Files
                    </Button>
                    <Button
                      type="button"
                      onClick={handleDownloadTemplateClick}
                      variant={'outline'}
                    >
                      <Download />
                      Template
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="w-[150px]"
                      disabled={
                        !isValid ||
                        isCreateDevicesPending ||
                        isCreateDeviceApiKeysPending ||
                        formValues.devices?.length === 0
                      }
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant={'outline'}
                      className="w-[150px]"
                      disabled={
                        isCreateDevicesPending || isCreateDeviceApiKeysPending
                      }
                      onClick={() => onToggleOpen()}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
